<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div>
    <div
      class="site-footer"
      :class="[
        `section-content-mobile--${footerTheme.mobile.content}`,
        `section-content-desktop--${footerTheme.desktop.content}`,
      ]"
    >
      <div
        class="site-footer__logo"
      >
        <div
          class="site-footer__logo-primary"
          v-html="$variable('design.logo_icon_primary')"
        />
        <div
          class="site-footer__logo-secondary"
          v-html="$variable('design.logo_icon_secondary')"
        />
      </div>
      <div
        v-if="isMobile"
        class="site-footer__nav-group"
        :class="{ [$class('open')]: isOpen }"
      >
        <div
          v-for="(item, index) of accordionItems"
          :key="`site-footer__item-${index}`"
          class="site-footer__accordion-item"
        >
          <button
            type="button"
            class="site-footer__accordion-toggle"
            :class="{ [$class('active')]: isActive(index)}"
            :aria-expanded="isActive(index)"
            @click="toggleAccordion(index)"
          >
            <span v-text="item.header" />
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.034 6.5L8.01699 9.51699L5 6.5"
                stroke="#090909"
              />
            </svg>
          </button>

          <div
            v-show="isActive(index)"
            class="site-footer__accordion-content"
          >
            <link-list
              :navigation-class="item.class"
              :navigation-menu="item.menu"
            />
            <div
              v-if="item.class === 'client_service'"
              class="site-footer__accordion-content__info"
              v-html="$string('general.site_footer.client_service_info')"
            />
          </div>
        </div>
      </div>
      <template v-if="!isMobile">
        <div
          v-if="navigation.client_service.length"
          class="site-footer__client_service"
        >
          <link-list
            :navigation-menu="navigation.client_service"
          />
        </div>
        <div
          v-if="navigation.company.length"
          class="site-footer__company"
        >
          <link-list
            :navigation-menu="navigation.company"
          />
        </div>
        <div
          v-if="navigation.social_media.length"
          class="site-footer__social_media"
        >
          <link-list
            :navigation-menu="navigation.social_media"
          />
        </div>
      </template>
      <div class="site-footer__bar">
        <span
          role="button"
          aria-pressed="false"
          class="site-footer__newsletter-button-desktop"
          @click="newsletterClick"
          v-text="$string('general.site_footer.newsletter_button')"
        />
        <btn
          class="button button--block site-footer__newsletter-button-mobile"
          type="button"
          :label="$string('general.site_footer.newsletter_button')"
          @click="newsletterClick"
          v-text="$string('general.site_footer.newsletter_button')"
        />
        <div class="site-footer__legal-container">
          <div
            class="site-footer__copyright site-footer__element--hide-on-mobile"
            v-html="$string('general.site_footer.copyright')"
          />
          <div
            v-if="navigation.legal.length"
            class="site-footer__legal site-footer__element--hide-on-mobile"
          >
            <link-list
              navigation-class="legal"
              :navigation-menu="navigation.legal"
            />
          </div>
        </div>
        <div class="site-footer__country">
          <country-selector
            class="site-footer__item"
            block-class="site-footer"
          />
        </div>
      </div>
      <div
        v-if="localizedIconsData"
        class="site-footer__bar"
      >
        <div />
        <ul class="payment-icons site-footer__legal-container">
          <template
            v-for="(icon, index) of localizedIconsData"
            :key="index"
          >
            <template
              v-for="(svg, index2) of icon.payment_logos"
              :key="index2"
            >
              <li class="payment-icons__icon">
                <img
                  :src="svg"
                  :alt="icon.country_code"
                >
              </li>
            </template>
          </template>
        </ul>

        <ul class="delivery-icons">
          <template
            v-for="(icon, index) of localizedIconsData"
            :key="index"
          >
            <template
              v-for="(svg, index2) of icon.delivery_logos"
              :key="index2"
            >
              <li class="delivery-icons__icon">
                <img
                  :src="svg"
                  :alt="icon.country_code"
                >
              </li>
            </template>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer
 * -----------------------------------------------------------------------------
 * Site footer component with accordion display of links.
 *
 * @param {Object} footerTheme - Background and content themes for the footer.
 * @param {Object} navigation - Menus to be displayed in footer.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import { throttle } from '~/helpers/general'
import CountrySelector from '~async/country-selector/country-selector'
import LinkList from '~async/site-footer/link-list/link-list'
import Btn from '~global/btn/btn'

export default {
  name: 'SiteFooter',

  components: {
    CountrySelector,
    LinkList,
    Btn,
  },

  props: {

    footerTheme: {
      type: Object,
      default: () => ({
        desktop: {
          background: '',
          content: '',
        },
        mobile: {
          background: '',
          content: '',
        },
      }),
    },

    navigation: {
      type: Object,
      default: () => ({
        social_media: '',
        client_service: '',
        assistance: '',
        company: '',
        legal: '',
      }),
    },

    localizedIcons: {
      type: Object || String,
      required: false,
      default: '',
    },

    marketHandle: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'footer-updated': () => true,
    'newsletter-clicked': () => true,
  },

  data() {
    return {
      adjusted: false,
      footerStatus: 'fixed',
      country_code: '',
      accordionItems: [
        {
          active: false,
          header: this.$string('general.site_footer.company'),
          class: 'company',
          menu: this.navigation.company,
        },
        {
          active: false,
          header: this.$string('general.site_footer.legal'),
          class: 'legal',
          menu: this.navigation.legal,
        },
        {
          active: false,
          header: this.$string('general.site_footer.client_service'),
          class: 'client_service',
          menu: this.navigation.client_service,
        },
        {
          active: false,
          header: this.$string('general.site_footer.social_media'),
          class: 'social_media',
          menu: this.navigation.social_media,
        },
      ],
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
      screenHeight: (state) => state.index.screen.height,
      screenWidth: (state) => state.index.screen.width,
      visibleHeight: (state) => state.header.visibleHeight,
    }),

    /**
     * Check if any accordion item is currently active.
     * @returns {Boolean}
     */
    isOpen() {
      return this.accordionItems.some((item) => item.active === true)
    },

    localizedIconsData() {
      return this.localizedIcons.data.filter((icon) => icon.country_code === this.marketHandle)
    },
  },

  mounted() {
    this.setFallbackStyles()
    this.setScrollEventListener()
    this.$nextTick().then(() => {
      cnvs.EventBus.emit('site-footer-loaded')
    })

    cnvs.EventBus.listen('ingrid-returns-clicked', () => {
      this.handleIngridModal()
    })
  },

  methods: {

    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    newsletterClick() {
      cnvs.EventBus.emit('newsletter-clicked')

    },

    /**
     * Sets the scroll event listener.
     */
    setScrollEventListener() {
      const mainElement = document.querySelector('#main-content')
      window.addEventListener(
        'scroll',
        throttle(() => this.handleWindowScroll(mainElement), 8),
      )
    },

    /**
     * Shifts elements up that should not overlap the footer.
     * - targets elements with the attribute [js-overlap-element]
     * @param {Number} offset - The number of pixels to shift the element by.
     */
    moveOverlappingElements(mainBottom) {
      if (this.getActiveOverlay) {
        return
      }

      const elements = [...document.querySelectorAll('[js-overlap-element]')]
      elements.forEach(element => {

        if (element.classList.contains('site-header')) {
          return
        }

        if (element.getAttribute('js-overlap-element') === 'false') {
          return
        }

        if (element.getAttribute('js-overlap-element') === 'hide') {
          element.style.visibility = 'hidden'
          return
        }

        const elementOffset = window.innerHeight - mainBottom
        element.style.transform = `translateY(-${elementOffset}px)`
      })
    },

    /**
     * Callback for window scroll event.
     * @param {Object} main - HTML Element.
     */
    handleWindowScroll(main) {
      const rect = main.getBoundingClientRect()
      const mainBottom = rect.bottom

      /**
       * Reset the main element translateY position when the main element is
       * fully on screen. This helps to correct any issues with scrolling too
       * quickly back up the page. For some reason if there is any inline
       * transform property set, then the pdp background blur does not work
       * correctly.
       */
      if (mainBottom > window.innerHeight) {
        if (!this.adjusted) {
          this.adjustFixedElements()
        }
        return
      }

      this.adjusted = false
      this.footerStatus = mainBottom <= 1 ? 'scroll' : 'fixed'
      this.moveOverlappingElements(mainBottom)
    },

    /**
     * Reset the properties of moved elements when the footer has been fully
     * hidden.
     */
    adjustFixedElements() {
      const elements = [...document.querySelectorAll('[js-overlap-element]')]
      elements.forEach(element => {

        if (element.classList.contains('site-header')) {
          return
        }

        if (element.getAttribute('js-overlap-element') === 'false') {
          return
        }

        if (element.getAttribute('js-overlap-element') === 'hide') {
          element.style.removeProperty('visibility')
          return
        }

        element.style.removeProperty('transform')
      })
      this.adjusted = true
    },

    /**
     * Sets fallback styles for the header.
     */
    setFallbackStyles() {
      document.documentElement.style.setProperty(
        '--footer-bg-mobile',
        this.footerTheme.mobile.background,
      )

      document.documentElement.style.setProperty(
        '--footer-bg-desktop',
        this.footerTheme.desktop.background,
      )
    },

    /**
     * Check if an accordion item is active using its index.
     * @param {Number} index - Index of selected accordion item.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.accordionItems[index].active
    },

    /**
     * Toggle accordion item active state.
     * @param {Number} currentIndex - Index of selected accordion item.
     */
    toggleAccordion(currentIndex) {
      if (this.isActive(currentIndex)) {
        this.accordionItems[currentIndex].active = false
        return
      }

      this.accordionItems.forEach((item, index) => {
        item.active = currentIndex === index
      })

      this.$emit('footer-updated')
    },

    handleIngridModal() {
      this.openOverlay({
        component: 'ingrid-returns-modal',
        ignoreDismissed: true,
        namespace: 'ingridReturnsModal',
      })

      // From Ingrid - adjusted to add in early returns
      window.addEventListener('message', (event) => {
        if (event?.data?.action !== 'ingrid-returns-iframe-size-change') {
          return
        }

        const iframe = window.document.getElementById('ingrid-returns-iframe')
        const ingridModal = document.querySelector('.ingrid-returns-modal__overlay')

        if (event.source !== iframe?.contentWindow) {
          return
        }

        iframe.style.height = `${event?.data?.height}px`
        // Magic number to match modal padding.
        ingridModal.style.height = `calc(${event?.data?.height}px + 64px)`
      })
    },
  },

}
</script>

<style lang="scss">
@import "./site-footer";
</style>
